import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2, X } from 'lucide-react';
import BodyPortal from './BodyPortal';

/**
 * In-app dialogs (themed, keyboard-accessible) used instead of the browser's
 * window.confirm / window.prompt.
 */

interface DialogShellProps {
  title: string;
  onCancel: () => void;
  children: React.ReactNode;
  busy?: boolean;
  /** Dialog width in px (default 400); never wider than the screen. */
  width?: number;
}

export const DialogShell: React.FC<DialogShellProps> = ({ title, onCancel, children, busy, width = 400 }) => {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || busy) return;
      // With dialogs stacked (e.g. the link dialog over the edit dialog) only the top one closes.
      const open = document.querySelectorAll('[role="dialog"][aria-modal="true"]');
      if (open.length && open[open.length - 1] !== dialogRef.current) return;
      onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, busy]);

  return (
    <BodyPortal>
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 10000, padding: 16,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.5)',
      }}
      onClick={() => !busy && onCancel()}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--card-bg, var(--card, #fff))',
          color: 'var(--card-foreground, var(--text-primary, #111))',
          borderRadius: 12, width, maxWidth: '100%', maxHeight: '100%', overflowY: 'auto', boxSizing: 'border-box',
          boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
          padding: 20, position: 'relative',
        }}
      >
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          aria-label={t('close', 'Close')}
          style={{
            position: 'absolute', top: 12, right: 12, padding: 4,
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text-secondary, #6b7280)',
          }}
        >
          <X size={16} />
        </button>
        {children}
      </div>
    </div>
    </BodyPortal>
  );
};

const buttonBase: React.CSSProperties = {
  padding: '8px 16px', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer',
};

export const DialogActions: React.FC<{
  confirmLabel: string;
  cancelLabel: string;
  danger?: boolean;
  busy?: boolean;
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  confirmRef?: React.Ref<HTMLButtonElement>;
}> = ({ confirmLabel, cancelLabel, danger, busy, confirmDisabled, onConfirm, onCancel, confirmRef }) => (
  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
    <button
      type="button"
      onClick={onCancel}
      disabled={busy}
      style={{
        ...buttonBase,
        background: 'transparent',
        color: 'var(--text-primary, #111)',
        border: '1px solid var(--theme-border, #e5e7eb)',
      }}
    >
      {cancelLabel}
    </button>
    <button
      ref={confirmRef}
      type="button"
      onClick={onConfirm}
      disabled={busy || confirmDisabled}
      style={{
        ...buttonBase,
        border: 'none', color: danger ? '#fff' : 'var(--button-primary-foreground, #fff)',
        background: danger ? 'var(--theme-error, #dc2626)' : 'var(--theme-primary, #1976d2)',
        opacity: busy || confirmDisabled ? 0.6 : 1,
        cursor: busy || confirmDisabled ? 'default' : 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 6,
      }}
    >
      {busy && <Loader2 size={14} className="animate-spin" aria-hidden />}
      {confirmLabel}
    </button>
  </div>
);

interface ConfirmDialogProps {
  title: string;
  message: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** Red confirm button and warning icon, for destructive actions. */
  danger?: boolean;
  /** Can return a promise — the dialog stays open (buttons disabled) until it settles. */
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  title, message, confirmLabel, cancelLabel, danger, onConfirm, onCancel,
}) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => { confirmRef.current?.focus(); }, []);

  const confirmingRef = useRef(false);
  const handleConfirm = async () => {
    if (confirmingRef.current) return;
    confirmingRef.current = true;
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      confirmingRef.current = false;
      setBusy(false);
    }
  };

  return (
    <DialogShell title={title} onCancel={onCancel} busy={busy}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', paddingRight: 20 }}>
        {danger && (
          <AlertTriangle size={20} style={{ color: 'var(--theme-error, #dc2626)', flexShrink: 0, marginTop: 2 }} aria-hidden />
        )}
        <div>
          <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 600 }}>{title}</h3>
          <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--text-secondary, #6b7280)' }}>{message}</div>
        </div>
      </div>
      <DialogActions
        confirmRef={confirmRef}
        confirmLabel={confirmLabel}
        cancelLabel={cancelLabel ?? t('cancel', 'Cancel')}
        danger={danger}
        busy={busy}
        onConfirm={handleConfirm}
        onCancel={onCancel}
      />
    </DialogShell>
  );
};

interface TextInputDialogProps {
  title: string;
  label: string;
  placeholder?: string;
  confirmLabel: string;
  cancelLabel?: string;
  maxLength?: number;
  onConfirm: (value: string) => void | Promise<void>;
  onCancel: () => void;
}

/** Single-field form dialog (replaces window.prompt). Empty / whitespace-only values can't be submitted. */
export const TextInputDialog: React.FC<TextInputDialogProps> = ({
  title, label, placeholder, confirmLabel, cancelLabel, maxLength = 120, onConfirm, onCancel,
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Guards against a second submit (Enter + click) before `busy` has re-rendered.
  const submittingRef = useRef(false);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const trimmed = value.trim();
  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!trimmed || submittingRef.current) return;
    submittingRef.current = true;
    setBusy(true);
    try {
      await onConfirm(trimmed);
    } finally {
      submittingRef.current = false;
      setBusy(false);
    }
  };

  return (
    <DialogShell title={title} onCancel={onCancel} busy={busy}>
      <form onSubmit={submit}>
        <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, paddingRight: 20 }}>{title}</h3>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 6, color: 'var(--text-secondary, #6b7280)' }}>
          {label}
          <input
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            maxLength={maxLength}
            disabled={busy}
            style={{
              display: 'block', width: '100%', marginTop: 6, boxSizing: 'border-box',
              padding: '8px 10px', borderRadius: 6, fontSize: 14,
              border: '1px solid var(--theme-border, #d1d5db)',
              background: 'var(--input-bg, var(--card-bg, #fff))',
              color: 'var(--text-primary, #111)',
            }}
          />
        </label>
        <DialogActions
          confirmLabel={confirmLabel}
          cancelLabel={cancelLabel ?? t('cancel', 'Cancel')}
          busy={busy}
          confirmDisabled={!trimmed}
          onConfirm={() => submit()}
          onCancel={onCancel}
        />
      </form>
    </DialogShell>
  );
};

export default ConfirmDialog;
